/* eslint-disable max-lines-per-function */
import React from 'react';
import { Container, Box } from '@mui/material';
import { useLang } from '~/hooks/useLang';
import { useHookstate } from '@hookstate/core';
import { random } from 'lodash';

export interface Totals {
  readonly name: string;
  readonly total_occurrences: number;
  readonly total_adults_arrested: number;
  readonly total_minors_seized: number;
  readonly total_weapons_seized: number;
  readonly total_ammunition_seized: number;
  readonly total_drugs_seized: number;
  readonly total_vehicles_seized: number;
  readonly total_vehicles_recovered: number;
}

export interface Data {
  readonly name: string;
  readonly total_occurrences: number;
  readonly total_adults_arrested: number;
  readonly total_minors_seized: number;
  readonly total_weapons_seized: number;
  readonly total_ammunition_seized: number;
  readonly total_drugs_seized: number;
  readonly total_vehicles_seized: number;
  readonly total_vehicles_recovered: number;
}

interface IndicatorsContentSummaryProps {
  readonly groupedBy: string
  readonly indicator?: {
    readonly totals: Totals;
    readonly data: readonly Data[]
  };
}

const tableStyles = {
  container: {
    width: '100%',
    border: '1px solid #000',
    borderCollapse: 'collapse' as const,
    fontSize: '12px',
    '@media print': {
      fontSize: '10px',
      width: '100%',
      tableLayout: 'fixed' as const,
    },
  },
  headerCell: {
    border: '1px solid #000',
    padding: '8px 4px',
    fontWeight: 'bold',
    textAlign: 'center' as const,
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    '@media print': {
      padding: '4px 2px',
      fontSize: '10px',
      backgroundColor: 'transparent',
    },
  },
  firstHeaderCell: {
    border: '1px solid #000',
    padding: '8px 4px',
    fontWeight: 'bold',
    textAlign: 'start' as const,
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    '@media print': {
      padding: '4px 2px',
      fontSize: '10px',
      backgroundColor: 'transparent',
    },
  },
  dataCell: {
    border: '1px solid #000',
    padding: '8px 4px',
    textAlign: 'center' as const,
    color: '#000000DE',
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    wordWrap: 'break-word' as const,
    '@media print': {
      padding: '3px 2px',
      fontSize: '0.875rem',
    },
  },
  firstDataCell: {
    border: '1px solid #000',
    padding: '8px 4px',
    textAlign: 'left' as const,
    color: '#000000DE',
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    wordWrap: 'break-word' as const,
    '@media print': {
      padding: '3px 2px',
      fontSize: '0.875rem',
    },
  },
}

export default function IndicatorsContentSummary({ indicator, groupedBy }: IndicatorsContentSummaryProps) {
  const { translate } = useLang();

  return (
    <Container fixed={false}>
      <Box component="table" sx={tableStyles.container}>
        <Box component="thead">
          <Box component="tr">
            <Box
              component="th"
              sx={{ ...tableStyles.firstHeaderCell, width: '33%' }}
            >
              {translate(groupedBy)}
            </Box>
            <Box
              component="th"
              sx={{ ...tableStyles.headerCell, width: '7.4%' }}
            >
              OCS
            </Box>
            <Box
              component="th"
              sx={{ ...tableStyles.headerCell, width: '7.4%' }}
            >
              ADA
            </Box>
            <Box
              component="th"
              sx={{ ...tableStyles.headerCell, width: '7.4%' }}
            >
              MNA
            </Box>
            <Box
              component="th"
              sx={{ ...tableStyles.headerCell, width: '7.4%' }}
            >
              AMA
            </Box>
            <Box
              component="th"
              sx={{ ...tableStyles.headerCell, width: '7.4%' }}
            >
              MUA
            </Box>
            <Box
              component="th"
              sx={{ ...tableStyles.headerCell, width: '7.4%' }}
            >
              DGA
            </Box>
            <Box
              component="th"
              sx={{ ...tableStyles.headerCell, width: '7.4%' }}
            >
              VEA
            </Box>
            <Box component="th" sx={{ ...tableStyles.headerCell, width: '7.4%' }}>
              VER
            </Box>
          </Box>
        </Box>
        <Box component="tbody">
          {indicator?.data.map(row => (
            <Box component="tr" key={random()}>
              <Box component="td" sx={tableStyles.firstDataCell}>
                {row.name}
              </Box>
              <Box component="td" sx={tableStyles.dataCell}>
                {row.total_occurrences}
              </Box>
              <Box component="td" sx={tableStyles.dataCell}>
                {row.total_adults_arrested}
              </Box>
              <Box component="td" sx={tableStyles.dataCell}>
                {row.total_minors_seized}
              </Box>
              <Box component="td" sx={tableStyles.dataCell}>
                {row.total_weapons_seized}
              </Box>
              <Box component="td" sx={tableStyles.dataCell}>
                {row.total_ammunition_seized}
              </Box>
              <Box component="td" sx={tableStyles.dataCell}>
                {row.total_drugs_seized}
              </Box>
              <Box component="td" sx={tableStyles.dataCell}>
                {row.total_vehicles_seized}
              </Box>
              <Box component="td" sx={tableStyles.dataCell}>
                {row.total_vehicles_recovered}
              </Box>
            </Box>
          ))}
          <Box component="tr">
            <Box component="td" sx={tableStyles.firstDataCell}>
              <strong>
                {indicator?.totals.name}
              </strong>
            </Box>
            <Box component="td" sx={tableStyles.dataCell}>
              <strong>
                {indicator?.totals.total_occurrences}
              </strong>
            </Box>
            <Box component="td" sx={tableStyles.dataCell}>
              <strong>
                {indicator?.totals.total_adults_arrested}
              </strong>
            </Box>
            <Box component="td" sx={tableStyles.dataCell}>
              <strong>
                {indicator?.totals.total_minors_seized}
              </strong>
            </Box>
            <Box component="td" sx={tableStyles.dataCell}>
              <strong>
                {indicator?.totals.total_weapons_seized}
              </strong>
            </Box>
            <Box component="td" sx={tableStyles.dataCell}>
              <strong>
                {indicator?.totals.total_ammunition_seized}
              </strong>
            </Box>
            <Box component="td" sx={tableStyles.dataCell}>
              <strong>
                {indicator?.totals.total_drugs_seized}
              </strong>
            </Box>
            <Box component="td" sx={tableStyles.dataCell}>
              <strong>
                {indicator?.totals.total_vehicles_seized}
              </strong>
            </Box>
            <Box component="td" sx={tableStyles.dataCell}>
              <strong>
                {indicator?.totals.total_vehicles_recovered}
              </strong>
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}